export interface LandingUser {
  source?: string;
  permissions?: string[];
  roles?: { permissions?: string[] }[];
  portals?: { audienceType?: string }[];
}

// Temporary customer rollout switch. Keep SSO entitlements intact for My Licenses.
export const CUSTOMER_PORTALS_ENABLED = false;

export function canShowAccountPortals(user: LandingUser | null): boolean {
  return !!user && (CUSTOMER_PORTALS_ENABLED || isStaffUser(user));
}

function effectivePermissions(user: LandingUser): string[] {
  // An explicitly resolved list wins over stale direct-role bindings.
  return Array.isArray(user.permissions)
    ? user.permissions
    : (user.roles || []).flatMap(role => role.permissions || []);
}

/** Navigation only. API permissions remain the authority for every operation. */
export function isStaffUser(user: LandingUser | null): boolean {
  if (!user) return false;
  const permissions = effectivePermissions(user);
  return permissions.some(permission =>
    permission === '*' || permission === 'platform.admin' ||
    permission === 'account.customers.read' ||
    /^(users|roles|groups|iam|settings|integrations|automations|intent|audit|tools|portals)\./.test(permission),
  ) || user.source === 'azure-ad' ||
    (user.portals || []).some(portal => portal.audienceType === 'internal');
}

export function defaultLanding(user: LandingUser | null): string {
  if (isStaffUser(user)) return '/dashboard';
  return user && effectivePermissions(user).some(permission => permission.startsWith('account.'))
    ? '/my-account'
    : '/dashboard';
}

export function resolveLanding(user: LandingUser | null, requested?: string | null): string {
  const fallback = defaultLanding(user);
  // Preserve local SSO/deep links, but never accept external or ambiguous URLs.
  if (!requested || !requested.startsWith('/') || /^\/\//.test(requested) || /[\\\r\n]/.test(requested)) return fallback;
  const parsed = new URL(requested, 'https://onegate.invalid');
  if (parsed.origin !== 'https://onegate.invalid') return fallback;
  const pathname = parsed.pathname;
  if (pathname === '/' || pathname === '/dashboard') return fallback;
  if ((pathname === '/my-account' || pathname.startsWith('/my-account/')) && isStaffUser(user)) return '/dashboard';
  if (pathname === '/my-account' && parsed.searchParams.get('tab')?.toLowerCase() === 'portals' && !canShowAccountPortals(user)) {
    parsed.searchParams.set('tab', 'overview');
  }
  return `${pathname}${parsed.search}${parsed.hash}`;
}
